// Si REACT_APP_API_URL no está seteada, usamos el mismo host desde el que se
// abrió la página (localhost en la compu, la IP de la red al abrir desde el
// celular) para no tener que reconfigurar nada al probar en otro dispositivo.
const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:4000/api`;

// El access token vive solo en memoria (no en localStorage): un XSS ya no
// puede robar una sesión de semanas, como mucho el access token vigente en
// ese momento (15 min). La sesión larga vive en el refresh token, en una
// cookie httpOnly que este código ni siquiera puede leer — se recupera al
// arrancar la app llamando a /auth/refresh (ver AuthContext), no leyendo
// storage.
let accessToken: string | null = null;

export function getToken(): string | null {
  return accessToken;
}

export function setToken(token: string): void {
  accessToken = token;
}

export function clearToken(): void {
  accessToken = null;
}

// Rutas cuya propia respuesta 401 no debe disparar un refresh + reintento:
// login/register fallan por credenciales inválidas (no por token vencido), y
// refresh no puede reintentarse a sí mismo sin entrar en loop.
const NO_REFRESH_RETRY_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/webauthn/login-verify'];

// Dedupe: si varios requests en paralelo pegan un 401 a la vez (token recién
// vencido), que disparen un solo refresh compartido en vez de uno cada uno.
let refreshInFlight: Promise<boolean> | null = null;

async function silentRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_URL}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = await res.json().catch(() => null);
        if (typeof data?.token !== 'string') return false;
        setToken(data.token);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

const CONNECTION_ERROR = 'No se pudo conectar con el servidor. Revisá tu conexión a internet e intentá de nuevo.';

// fetch con el access token y, si vuelve 401 por token vencido, un refresh +
// reintento. Lo comparten apiRequest (JSON) y apiStream (server-sent events).
async function authedFetch(path: string, options: RequestInit, isRetry = false): Promise<Response> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
  } catch (err) {
    // Lo cortó el propio cliente (botón "detener"): no es un problema de conexión.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // fetch() rechaza (sin llegar a haber respuesta) cuando no hay conexión,
    // el servidor está caído, o lo bloquea CORS — ahí el navegador tira un
    // "Failed to fetch" en inglés que no le dice nada al usuario.
    throw new Error(CONNECTION_ERROR);
  }

  if (res.status === 401 && !isRetry && !NO_REFRESH_RETRY_PATHS.includes(path)) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      return authedFetch(path, options, true);
    }
    clearToken();
  }
  return res;
}

export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await authedFetch(path, options);
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data?.error ?? 'No se pudo conectar con el servidor.');
  }

  return data as T;
}

// Para endpoints que responden server-sent events: llama a `onEvent` con cada
// evento a medida que llega y termina cuando el servidor cierra la respuesta.
// Si el servidor rechaza el pedido antes de empezar (400, 429, 503...), viene
// como JSON normal y se lanza su mensaje de error, igual que apiRequest.
export async function apiStream(
  path: string,
  options: RequestInit,
  onEvent: (event: string, data: unknown) => void
): Promise<void> {
  const res = await authedFetch(path, { ...options, headers: { Accept: 'text/event-stream', ...options.headers } });
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error ?? 'No se pudo conectar con el servidor.');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const dispatch = (block: string) => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    if (dataLines.length === 0) return;
    try {
      onEvent(event, JSON.parse(dataLines.join('\n')));
    } catch {
      // Un evento que no es JSON válido no debería pasar; se ignora.
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let sep = buffer.indexOf('\n\n');
      while (sep !== -1) {
        dispatch(buffer.slice(0, sep));
        buffer = buffer.slice(sep + 2);
        sep = buffer.indexOf('\n\n');
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new Error(CONNECTION_ERROR);
  }
  if (buffer.trim()) dispatch(buffer);
}
